<template>
	<section
		v-if="items.length"
		class="solution-service-m"
		:class="{ 'solution-service-m--area': isAreaTeacher }"
	>
		<ul class="solution-service-m__list" role="list">
			<li
				v-for="(item, index) in items"
				:key="item.id"
				:ref="el => bindSolutionImpression(el, item, index)"
				class="solution-card"
				role="listitem"
				@click="toServiceDetail(item, index)"
			>
				<img
					v-if="isAreaTeacher"
					class="solution-card__poster"
					:src="item.cover || defaultCover"
					:alt="item.name"
				/>

				<template v-else>
					<img class="solution-card__image" :src="item.cover || defaultCover" :alt="item.name" />
					<div class="solution-card__body">
						<h3 class="solution-card__name">{{ item.name }}</h3>
						<p class="solution-card__detail">{{ item.detail }}</p>

						<p v-if="item.discount_tag" class="solution-card__original">
							{{ t(`${I18N_NS}.originalPrice`, { price: item.price }) }}
						</p>

						<footer class="solution-card__footer">
							<div class="solution-card__price">
								<img src="~/assets/images/pc/coin.png" :alt="t(`${I18N_NS}.coinAlt`)" />
								<span>{{ item.final_price }}</span>
							</div>
							<button
								type="button"
								class="solution-card__btn"
								:aria-label="t(`${I18N_NS}.serviceOrder`)"
								@click.stop="order(item, index)"
							>
								<span class="solution-card__plus" aria-hidden="true"></span>
							</button>
						</footer>
					</div>
				</template>
			</li>
		</ul>
	</section>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, ref } from "vue";
import defaultCover from "~/assets/images/default.png";
import { orderConsultant } from "~/api/api";
import { PayBox } from "~/hooks/pay/index";
import { useI18n } from "~/composables/useI18n";
import { useInjectedMentorDetailTracking } from "@variants/seer/teacher-detail/composables/useMentorDetailTracking";

const I18N_NS = "teacherDetail.pc";
const { t, locale } = useI18n();
const openLogin = inject("openLogin");
const tracking = useInjectedMentorDetailTracking();
const impressionTracker = tracking?.createImpressionTracker?.();

const props = defineProps({
	data: {
		type: Array,
		default: () => [],
	},
	variant: {
		type: String,
		default: "default",
	},
});

const route = useRoute();
const isOrder = ref(false);
const isAreaTeacher = computed(() => props.variant === "area");
const items = computed(() => (Array.isArray(props.data) ? props.data.map(formatServiceItem) : []));

function bindSolutionImpression(el, item, index) {
	if (!el || !impressionTracker || !item?.id) return;
	impressionTracker.bind(el, `solution_${item.id}`, "mentor_solution_card_impression", {
		solution_id: String(item.id),
		price_bucket: tracking.getPriceBucket(item.final_price ?? item.price),
		position: index + 1,
	});
}

const toServiceDetail = async (item, index) => {
	tracking?.track?.("mentor_solution_add_click", {
		solution_id: String(item.id),
		price_bucket: tracking.getPriceBucket(item.final_price ?? item.price),
		position: typeof index === "number" ? index + 1 : undefined,
		action_type: "card",
	});
	await navigateTo(`/seer/teacher-service/${route.params.id}/${item.id}`);
};

const order = async (item, index) => {
	openLogin?.();
	if (isOrder.value) return;
	isOrder.value = true;

	try {
		const res = await orderConsultant({
			teacher_uid: route.params.id,
			service_id: item.id,
		});

		if (import.meta.client) {
			const { logEvent } = await import("~/utils/firebase");
			logEvent("site_masterpage_pay");
		}
		tracking?.track?.("mentor_solution_add_click", {
			solution_id: String(item.id),
			price_bucket: tracking.getPriceBucket(item.final_price ?? item.price),
			position: typeof index === "number" ? index + 1 : undefined,
			action_type: "add",
		});

		PayBox.alter({
			orderInfo: res.data,
			finalPrice: res.data.final_amount,
		});
	} catch (error) {
		console.error("Order failed:", error);
	} finally {
		isOrder.value = false;
	}
};

onBeforeUnmount(() => {
	impressionTracker?.reset?.();
});

function formatServiceItem(item) {
	return {
		...item,
		name: pickLocalized(item, "name"),
		detail: pickLocalizedDetail(item),
		cover: pickServiceImage(item),
	};
}

function pickLocalized(item, key) {
	const isEn = locale.value === "en";
	return (isEn ? item?.[`${key}_en`] : item?.[key]) || item?.[key] || "";
}

function pickLocalizedDetail(item) {
	if (locale.value === "en") {
		return item?.detail_en || item?.detail || "";
	}
	return item?.detail || "";
}

function pickServiceImage(item) {
	const candidates =
		locale.value === "en"
			? [item?.image, item?.background_image, item?.image_jt]
			: [item?.image_jt, item?.image, item?.background_image];
	return candidates.map(getFirstImageUrl).find(Boolean) || "";
}

function getFirstImageUrl(value) {
	if (Array.isArray(value)) {
		return value.map(getFirstImageUrl).find(Boolean) || "";
	}
	if (typeof value !== "string") return "";
	return value
		.split(",")
		.map(item => item.trim())
		.find(Boolean);
}
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	overflow-wrap: anywhere;
}

.solution-service-m {
	width: 100%;
	padding: 0 32px;
	font-family: "Source Han Serif CN", "PingFang SC", sans-serif;

	&__list {
		display: grid;
		grid-template-columns: 1fr;
		gap: 20px;
		margin: 0;
		padding: 0;
		list-style: none;
	}
}

.solution-card {
	display: flex;
	min-width: 0;
	padding: 16px;
	border: 1px solid #eeeeee;
	border-radius: 12px;
	background: #ffffff;
	cursor: pointer;

	&__image {
		flex: 0 0 auto;
		width: 220px;
		height: auto;
		aspect-ratio: 1 / 1;
		border-radius: 8px;
		object-fit: cover;
		background: #f3f3f3;
	}

	&__body {
		display: flex;
		flex: 1;
		min-width: 0;
		flex-direction: column;
		padding-left: 18px;
	}

	&__name {
		margin: 0;
		color: #333333;
		font-size: 30px;
		font-weight: 700;
		line-height: 1.35;
		@include line-clamp(1);
	}

	&__detail {
		margin: 10px 0 12px;
		color: #585858;
		font-family: "PingFang SC", sans-serif;
		font-size: 24px;
		line-height: 34px;
		@include line-clamp(2);
	}

	&__original {
		margin: 0 0 8px;
		color: #999999;
		font-size: 20px;
		line-height: 1.3;
	}

	&__footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		margin-top: auto;
	}

	&__price {
		display: flex;
		align-items: center;
		min-width: 0;
		color: #de2828;

		img {
			width: 30px;
			height: 30px;
			margin-right: 6px;
		}

		span {
			font-size: 30px;
			font-weight: 700;
			line-height: 1;
		}
	}

	&__btn {
		flex: 0 0 auto;
		position: relative;
		width: 40px;
		height: 40px;
		padding: 0;
		border: 0;
		border-radius: 50%;
		background: #333333;
		color: #ffffff;
	}

	&__plus {
		position: absolute;
		left: 50%;
		top: 50%;
		width: 18px;
		height: 18px;
		transform: translate(-50%, -50%);

		&::before,
		&::after {
			content: "";
			position: absolute;
			left: 50%;
			top: 50%;
			background: #ffffff;
			border-radius: 2px;
			transform: translate(-50%, -50%);
		}

		&::before {
			width: 18px;
			height: 3px;
		}

		&::after {
			width: 3px;
			height: 18px;
		}
	}

	&__poster {
		display: block;
		width: 100%;
		aspect-ratio: 686 / 866;
		border-radius: 8px;
		object-fit: cover;
		background: #f3f3f3;
	}
}

.solution-service-m--area {
	.solution-service-m__list {
		gap: 24px;
	}

	.solution-card {
		display: block;
		padding: 0;
		border: 0;
		background: transparent;
	}
}
</style>
